import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Inbox, CheckCircle, Clock, ArrowUpRight, TrendingUp, Activity, Timer } from 'lucide-react'
import { useDashboardStats, useEventHeaders, useEvents, useInboxes } from '@/api'
import { Link } from '@tanstack/react-router'
import { EventListItem } from '@/components/classification/event-list-item'

/**
 * Format seconds into a human-readable time string.
 * Scales gracefully from seconds to days.
 */
function formatTimeSaved(totalSeconds: number): string {
  if (totalSeconds < 60) {
    return `${totalSeconds}s`
  }
  
  const minutes = Math.floor(totalSeconds / 60)
  if (minutes < 60) {
    const seconds = totalSeconds % 60
    return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`
  }
  
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  if (hours < 24) {
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
  }
  
  const days = Math.floor(hours / 24)
  const remainingHours = hours % 24
  return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
}

type StatsCardProps = {
  title: string
  value: number | string
  description: string
  icon: React.ElementType
  trend?: string
  delay?: number
}

function StatsCard({
  title,
  value,
  description,
  icon: Icon,
  trend,
  delay = 0,
}: StatsCardProps) {
  return (
    <Card 
      className="group hover-lift overflow-hidden"
      style={{ animationDelay: `${delay}ms` }}
    >
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{title}</CardTitle>
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary transition-transform duration-300 group-hover:scale-110">
          <Icon className="h-4 w-4" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline gap-2">
          <div className="text-3xl font-bold tracking-tight">{value}</div>
          {trend && (
            <span className="flex items-center text-xs font-medium text-green-600">
              <TrendingUp className="mr-0.5 h-3 w-3" />
              {trend}
            </span>
          )}
        </div>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </CardContent>
    </Card>
  )
}

function StatsCardSkeleton({ delay = 0 }: { delay?: number }) {
  return (
    <Card style={{ animationDelay: `${delay}ms` }}>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-9 w-9 rounded-xl" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-9 w-20 mb-2" />
        <Skeleton className="h-3 w-32" />
      </CardContent>
    </Card>
  )
}

export function DashboardPage() {
  const { data: stats, isLoading: statsLoading } = useDashboardStats()
  const { data: recentEvents, isLoading: eventsLoading } = useEvents({ limit: 5 })
  // Paused inboxes are still connected; the stats endpoint counts only active ones.
  const { data: inboxes } = useInboxes()
  const headers = useEventHeaders(recentEvents?.events.map((event) => event.uuid) ?? [])
  const activeInboxes = inboxes?.filter((inbox) => inbox.is_active).length ?? 0

  return (
    <div className="space-y-8">
      {/* Page header */}
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold tracking-tight lg:text-3xl">Dashboard</h1>
        <p className="text-muted-foreground">
          Your email automation at a glance
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 stagger-children">
        {statsLoading ? (
          <>
            <StatsCardSkeleton delay={0} />
            <StatsCardSkeleton delay={50} />
            <StatsCardSkeleton delay={100} />
            <StatsCardSkeleton delay={150} />
          </>
        ) : (
          <>
            <StatsCard
              title="Connected Inboxes"
              value={(inboxes?.length ?? 0).toLocaleString()}
              description={`${activeInboxes.toLocaleString()} processing`}
              icon={Inbox}
              delay={0}
            />
            <StatsCard
              title="Emails Processed"
              value={(stats?.emailsProcessed ?? 0).toLocaleString()}
              description="Total emails handled"
              icon={CheckCircle}
              delay={50}
            />
            <StatsCard
              title="Time Saved"
              value={formatTimeSaved(stats?.timeSavedSeconds ?? 0)}
              description="By automating your inbox"
              icon={Timer}
              delay={100}
            />
            <StatsCard
              title="Events Today"
              value={(stats?.eventsToday ?? 0).toLocaleString()}
              description="Emails processed"
              icon={Clock}
              delay={150}
            />
          </>
        )}
      </div>

      {/* Recent Events */}
      <Card className="overflow-hidden">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Recent Events
            </CardTitle>
            <CardDescription>
              Latest email processing events
            </CardDescription>
          </div>
          <Link
            to="/events"
            className="flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            View all
            <ArrowUpRight className="h-3 w-3" />
          </Link>
        </CardHeader>
        <CardContent>
          {eventsLoading ? (
            <div className="space-y-4 stagger-children">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-4 p-4 rounded-lg border bg-muted/30">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-48" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                  <Skeleton className="h-6 w-16 rounded-full" />
                </div>
              ))}
            </div>
          ) : recentEvents && recentEvents.events.length > 0 ? (
            <div className="divide-y border-y">
              {recentEvents.events.map((event) => (
                <EventListItem
                  key={event.uuid}
                  event={event}
                  headers={headers.data ? (headers.data[event.uuid] ?? null) : headers.isError ? null : undefined}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted mb-4">
                <Activity className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold">No recent events</h3>
              <p className="mt-1 text-sm text-muted-foreground max-w-sm">
                Connect an inbox and define your classification policy to get started.
              </p>
              <Link
                to="/inboxes"
                className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
              >
                Connect your first inbox
                <ArrowUpRight className="h-3 w-3" />
              </Link>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
